import type { ReactNode } from 'react';

export type HelpKind = 'step' | 'field' | 'action' | 'disabled';
export type HelpDensity = 'rich' | 'compact';

export interface HelpContent {
  kind: HelpKind;
  title: string;
  summary: string;
  why?: string;
  downstream?: string;
  note?: string;
  prerequisite?: string;
  shortcut?: string;
  blockedReason?: string;
  nextStep?: string;
  inlineHint?: string;
  density?: HelpDensity;
}

export type HelpValue = HelpContent | ReactNode | string | null | undefined;

export const isHelpContent = (value: HelpValue): value is HelpContent => {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'kind' in value &&
      'title' in value &&
      'summary' in value
  );
};

export const getHelpPreviewText = (value: HelpValue): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (isHelpContent(value)) {
    return value.summary;
  }

  return null;
};

export const getInlineHelpHint = (value: HelpValue): string | null => {
  if (!isHelpContent(value)) {
    return null;
  }

  return value.inlineHint ?? null;
};

const buildHelp = (
  kind: HelpKind,
  title: string,
  summary: string,
  options: Omit<HelpContent, 'kind' | 'title' | 'summary'> = {}
): HelpContent => ({
  kind,
  title,
  summary,
  density: kind === 'action' || kind === 'disabled' ? 'compact' : 'rich',
  ...options,
});

export const createFieldHelp = (
  title: string,
  summary: string,
  options: Omit<HelpContent, 'kind' | 'title' | 'summary'> = {}
) => buildHelp('field', title, summary, options);

export const createStepHelp = (
  title: string,
  summary: string,
  options: Omit<HelpContent, 'kind' | 'title' | 'summary'> = {}
) => buildHelp('step', title, summary, options);

export const createActionHelp = (
  title: string,
  summary: string,
  options: Omit<HelpContent, 'kind' | 'title' | 'summary'> = {}
) => buildHelp('action', title, summary, options);

export const createDisabledHelp = (
  title: string,
  summary: string,
  options: Omit<HelpContent, 'kind' | 'title' | 'summary'> = {}
) => buildHelp('disabled', title, summary, options);
